import React from 'react';
import HoldingsTable from './HoldingsTable';
import Transaction from '../../data/models/Transaction';
import { IconStore } from '../../IconStore';
import InputField from '../ui-elements/InputField';
import DateField from '../ui-elements/DateField';
import Modal from '../ui-elements/Modal';

const bootstrap = global.bootstrap;

const IMPORT_CSV_TOOLTIP = 'CSV columns: Date, Ticker, Quantity, Unit Price<br>Date is optional (defaults to 90 days ago).';

function getDefaultImportDate() {
  const date = new Date();
  date.setDate(date.getDate() - 90);
  return date.toLocaleDateString();
}

function parseCsvLine(line) {
  const result = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      inQuotes = !inQuotes;
    } else if (ch === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += ch;
    }
  }

  result.push(current);
  return result;
}

function parseTransactionCsv(text) {
  const lines = text.trim().split(/\r?\n/).filter((line) => line.trim());
  if (lines.length < 2) {
    return [];
  }

  const headers = parseCsvLine(lines[0]).map((header) => header.trim().toLowerCase());
  const dateIdx = headers.indexOf('date');
  const tickerIdx = headers.indexOf('ticker');
  const quantityIdx = headers.indexOf('quantity');
  const priceIdx = headers.indexOf('unit price') !== -1
    ? headers.indexOf('unit price')
    : headers.indexOf('purchase price');

  if (tickerIdx === -1 || quantityIdx === -1 || priceIdx === -1) {
    throw new Error('CSV must contain Ticker, Quantity, and Unit Price columns.');
  }

  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const values = parseCsvLine(lines[i]);
    if (values.every((value) => !value.trim())) {
      continue;
    }

    const ticker = values[tickerIdx]?.trim();
    if (!ticker) {
      continue;
    }

    const row = {
      ticker,
      quantity: values[quantityIdx]?.trim().replace(/,/g, ''),
      purchasePrice: values[priceIdx]?.trim().replace(/,/g, '')
    };

    if (dateIdx !== -1 && values[dateIdx]?.trim()) {
      row.date = values[dateIdx].trim();
    }

    rows.push(row);
  }

  return rows;
}

function parseImportDate(dateStr, fallbackDateStr) {
  const parsed = new Date(dateStr);
  if (isNaN(parsed.getTime())) {
    return fallbackDateStr;
  }
  return parsed.toLocaleDateString();
}

function ImportProgressModal(props) {
  const percent = props.total > 0 ? Math.round((props.current / props.total) * 100) : 0;

  return (
    <div ref={props.modalRef} className="modal fade" id="modal-import-progress" tabIndex="-1" role="dialog" aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="h4 modal-title">Importing Transactions</h2>
          </div>
          <div className="modal-body">
            <p className="mb-3">Processing {props.current} of {props.total}...</p>
            <div className="progress import-progress-bar">
              <div className="progress-bar progress-bar-striped progress-bar-animated bg-success" role="progressbar"
                style={{ width: percent + '%' }} aria-valuenow={percent} aria-valuemin="0" aria-valuemax="100" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

class TransactionsView extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      transactionsData: {
        columns: [
          'Date',
          'Stock Ticker',
          'Unit Price',
          'No. of Shares'
        ],
        items: []
      },
      importProgress: null
    };

    this.fileInputRef = React.createRef();
    this.importButtonRef = React.createRef();
    this.importProgressModalRef = React.createRef();

    this.populateTable = this.populateTable.bind(this);
    this.addTransaction = this.addTransaction.bind(this);
    this.updateTransaction = this.updateTransaction.bind(this);
    this.deleteTransaction = this.deleteTransaction.bind(this);
    this.importTransactions = this.importTransactions.bind(this);
    this.importTransactionsFromRows = this.importTransactionsFromRows.bind(this);
    this.runImport = this.runImport.bind(this);
  }

  componentDidMount() {
    Transaction.fetch()
      .then((transactions) => this.populateTable(transactions));

    if (this.importButtonRef.current && bootstrap) {
      this.importTooltip = new bootstrap.Tooltip(this.importButtonRef.current, {
        html: true,
        title: IMPORT_CSV_TOOLTIP
      });
    }
  }

  componentWillUnmount() {
    this.disposeImportTooltip();
    this.disposeImportProgressModal();
  }

  disposeImportTooltip() {
    if (!this.importTooltip) {
      return;
    }

    const tooltip = this.importTooltip;
    this.importTooltip = null;

    try {
      tooltip.hide();
    } catch (error) {
      // Tooltip DOM may already be gone.
    }

    try {
      tooltip.dispose();
    } catch (error) {
      // Bootstrap alpha can throw if the tip element was already removed.
    }
  }

  showImportProgressModal(total) {
    return new Promise((resolve) => {
      this.setState({ importProgress: { current: 0, total } }, () => {
        const modalElement = this.importProgressModalRef.current;
        if (modalElement && bootstrap) {
          this.bootstrapImportModal = new bootstrap.Modal(modalElement, {
            backdrop: 'static',
            keyboard: false
          });
          this.bootstrapImportModal.show();
        }
        resolve();
      });
    });
  }

  updateImportProgress(current) {
    this.setState((prevState) => {
      if (!prevState.importProgress) {
        return null;
      }

      return {
        importProgress: {
          ...prevState.importProgress,
          current
        }
      };
    });
  }

  hideImportProgressModal() {
    return new Promise((resolve) => {
      if (!this.bootstrapImportModal || !this.importProgressModalRef.current) {
        this.bootstrapImportModal = null;
        if (this.state.importProgress) {
          this.setState({ importProgress: null }, resolve);
        } else {
          resolve();
        }
        return;
      }

      const modalElement = this.importProgressModalRef.current;
      const modal = this.bootstrapImportModal;

      const finishHide = () => {
        modalElement.removeEventListener('hidden.bs.modal', finishHide);
        try {
          modal.dispose();
        } catch (error) {
          // Modal DOM may already be gone.
        }
        this.bootstrapImportModal = null;
        if (this.state.importProgress) {
          this.setState({ importProgress: null }, resolve);
        } else {
          resolve();
        }
      };

      modalElement.addEventListener('hidden.bs.modal', finishHide);
      try {
        modal.hide();
      } catch (error) {
        finishHide();
      }
    });
  }

  disposeImportProgressModal() {
    if (!this.bootstrapImportModal) {
      return;
    }

    const modal = this.bootstrapImportModal;
    this.bootstrapImportModal = null;

    try {
      modal.hide();
      modal.dispose();
    } catch (error) {
      // Modal DOM may already be gone.
    }

    document.body.classList.remove('modal-open');
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('padding-right');
    document.querySelectorAll('.modal-backdrop').forEach((backdrop) => backdrop.remove());
  }

  populateTable(transactions) {
    const sortedTransactions = [...transactions].sort((a, b) => {
      const keyA = new Date(a.date);
      const keyB = new Date(b.date);
      if (keyA < keyB) return 1;
      if (keyA > keyB) return -1;
      return 0;
    });

    const items = sortedTransactions.map((txn) => txn.toJSON(true));

    this.setState({
      transactionsData: {
        ...this.state.transactionsData,
        items
      }
    });
  }

  addTransaction(event) {
    const modalData = event.modalData;
    new Transaction({
      date: modalData['TransactionDate'],
      stockTicker: modalData['StockTicker'],
      purchasePrice: modalData['UnitPrice'],
      numberOfShares: modalData['NoOfshares']
    }).save().then(() => this.populateTable(Transaction.findAll()));
  }

  updateTransaction(event) {
    const id = event.target.dataset.id;
    const txn = Transaction.find(id);

    const modalData = event.modalData;
    txn.purchasePrice = modalData['UnitPrice'];
    txn.numberOfShares = modalData['NoOfshares'];

    txn.save().then(() => this.populateTable(Transaction.findAll()));
  }

  deleteTransaction(event) {
    const id = event.target.dataset.id;
    Transaction.find(id).delete().then(() => this.populateTable(Transaction.findAll()));
  }

  importTransactions(event) {
    const file = event.target.files[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = (loadEvent) => {
      try {
        const rows = parseTransactionCsv(loadEvent.target.result);
        if (rows.length === 0) {
          window.alert('No transactions found in the CSV file.');
          return;
        }
        this.importTransactionsFromRows(rows);
      } catch (error) {
        window.alert(error.message);
      }
    };
    reader.readAsText(file);
  }

  importTransactionsFromRows(rows) {
    void this.runImport(rows);
  }

  async runImport(rows) {
    const total = rows.length;
    const defaultImportDate = getDefaultImportDate();

    await this.showImportProgressModal(total);

    let importFailed = false;
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];

      try {
        const date = row.date ? parseImportDate(row.date, defaultImportDate) : defaultImportDate;
        await new Transaction({
          date,
          stockTicker: row.ticker,
          purchasePrice: row.purchasePrice,
          numberOfShares: row.quantity
        }).save();
      } catch (error) {
        importFailed = true;
      }

      this.updateImportProgress(i + 1);
    }

    await this.hideImportProgressModal();

    if (importFailed) {
      window.alert('Failed to import one or more transactions.');
    }

    this.populateTable(Transaction.findAll());
  }

  render() {
    let content = <NoTransactionAlert />;
    if (this.state.transactionsData.items.length > 0) {
      content = <HoldingsTable data={this.state.transactionsData} />;
    }

    return (
      <span>
        <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
          <div>
            <button className="btn btn-primary btn-sm mr-2" data-toggle="modal" data-target="#modal-add-transaction">
              <span className={"mr-2 fas " + IconStore['plus']}></span>
              Add transaction
            </button>
            <button ref={this.importButtonRef} className="btn btn-secondary btn-sm mr-2" type="button"
              data-bs-toggle="tooltip" data-bs-html="true"
              onClick={() => this.fileInputRef.current.click()}>
              <span className={"mr-2 fas " + IconStore['fileImport']}></span>
              Import
            </button>
            <input ref={this.fileInputRef} type="file" accept=".csv,text/csv" className="d-none" onChange={this.importTransactions} />
          </div>
          <TransactionModals addTransaction={this.addTransaction}
            updateTransaction={this.updateTransaction}
            deleteTransaction={this.deleteTransaction}/>
        </div>
        {this.state.importProgress && (
          <ImportProgressModal modalRef={this.importProgressModalRef}
            current={this.state.importProgress.current}
            total={this.state.importProgress.total} />
        )}
        {content}
      </span>
    );
  }
}

function NoTransactionAlert(props) {
  return (
    <div className="alert alert-primary" role="alert">
      No Transactions. Click on "Add transaction" to add a transaction.
    </div>
  );
}

class TransactionModals extends React.Component {
  render() {
    return (
      <span>
        <Modal modalId="modal-add-transaction" title="Add Transaction" bodyElement={(<span>
          <InputField text="Stock Ticker" label="Stock Ticker" placeholder="Stock Ticker" />
          <DateField text="Transaction Date" />
          <InputField text="Unit Price" label="Unit Price" placeholder="Unit Price" />
          <InputField text="No Of shares" label="No. of shares" placeholder="No. of shares" />
          </span>)} actionButtonTitle="Add" onClick={this.props.addTransaction} />
        <Modal modalId="modal-edit-transaction" title="Edit Transaction" bodyElement={(<span>
              <InputField modelClass={Transaction.name} modelKey="purchasePrice" text="Unit Price" label="Unit Price" placeholder="Unit Price" />
              <InputField modelClass={Transaction.name} modelKey="numberOfShares" text="No Of shares" label="No. of shares" placeholder="No. of shares" />
              </span>)} actionButtonTitle="Update" onClick={this.props.updateTransaction} />
        <Modal modalId="modal-delete-transaction" title="Delete Transaction?"
          bodyElement={(<p>Are you sure you want to delete this transaction?</p>)}
          actionButtonTitle="Delete" destructive={true} onClick={this.props.deleteTransaction} />
      </span>
    );
  }
}

export default TransactionsView;
